import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/store';
import { Card, Notice, PageHeading, Spinner } from '../components/Shell';

/**
 * The other end of a staff invitation email.
 *
 * The token in the link is not on its own a credential: acceptInvite matches
 * it against the signed-in caller's own email address, so a forwarded link is
 * useless to whoever receives it. That is why this page asks the invitee to
 * sign in first rather than redeeming on arrival. It looks like an extra step
 * and it is the whole security property.
 *
 * A brand-new sitter has no account yet, so signing in here includes signing
 * up; either route comes back to this URL and the redemption runs then.
 */
export function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  // Strict mode mounts effects twice in development. Redeeming is a
  // single-use write, so the second run would report "already accepted" on a
  // link that had just worked.
  const attempted = useRef(false);

  useEffect(() => {
    if (!session.user || !token || attempted.current) return;
    attempted.current = true;

    void (async () => {
      try {
        await api('/staff/accept', { method: 'POST', body: { token } });
        // The new membership is what decides where this person may go next,
        // so it has to be in the session before we send them anywhere.
        await session.refreshMemberships();
        navigate('/admin', { replace: true });
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'That invitation could not be accepted. Ask the owner to send a new one.',
        );
      }
    })();
  }, [session, token, navigate]);

  if (!token) return <Navigate to="/" replace />;

  if (session.status === 'loading') {
    return (
      <div className="mx-auto max-w-md px-5 py-24">
        <Spinner label="Checking your invitation" />
      </div>
    );
  }

  // Not signed in yet: send them to sign in and come straight back here.
  if (!session.user) {
    return <Navigate to="/sign-in" state={{ from: location.pathname }} replace />;
  }

  return (
    <div className="mx-auto max-w-md px-5 py-16">
      <PageHeading title="Joining the team" />
      <Card>
        {error ? (
          <Notice tone="error" title="That did not work">{error}</Notice>
        ) : (
          <Spinner label="Accepting your invitation" />
        )}
      </Card>
    </div>
  );
}
